import React, { useEffect, useCallback, useMemo, useReducer, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Grid, GridToolbar, GridColumn as Column } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import CustomScrollbar from '@components/CustomScrollbar/CustomScrollbar';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import kendoDatatableParams from '@helpers/kendoDatatableParams';
import I18n from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import api from '@utils/axios';
import useDebounce from '@hooks/useDebounce';
import { datatableDeficienciesPath } from 'routes';
import { saveCacheToLocalStorage } from '@helpers/localStorage';
import { getInitialData, getInitialFilters } from './helpers/initialData';
import useFilters from './hooks/useFilters';
import ColumnMenu from './nested/ColumnMenu';
import ColumnMenuContext from './nested/ColumnMenuContext.tsx';
import MassActions from './nested/MassActions';
import './index.css';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import { actionTypes, reducer } from './reducer';
import { ALL_COLUMNS, ALL_COLUMNS_DATA, CACHE_KEY } from './constants';
import DeficiencyIdCell from './nested/DeficiencyIdCell.tsx';
import EmbeddedDeficiencyDetailBrushaway from './nested/EmbeddedDeficiencyDetailBrushaway.tsx';

const queryClient = new QueryClient();

function DeficienciesIndex({
  territoriesForSelect,
  resolutionStatusesForSelect,
  deficiencyStatusesForSelect,
  tagsForSelect,
  salesPeopleForSelect,
  assetsForSelect,
  screen,
  screenId,
  cacheKey
}) {
  const [state, dispatch] = useReducer(reducer, { cacheKey, screen, screenId }, getInitialData);
  const { page, sort, filters, columns, data, totalCount, selectAll, loading } = state;

  // Publish live grid state to the AI chat widget (page context overlay).
  usePublishListContext({ page, sort, filters, totalCount, data });
  const [selectedDeficiencyId, setSelectedDeficiencyId] = useState(null);
  const inspectionId = screen === 'inspection' ? screenId : undefined;
  const [isEmbeddedBrushawayOpen, setIsEmbeddedBrushawayOpen] = useState(false);

  const debouncedFilters = useDebounce(filters);

  useEffect(() => {
    saveCacheToLocalStorage(cacheKey, { page, sort, filters, columns });
  }, [page, sort, filters, columns]);

  useEffect(() => {
    if (filters !== debouncedFilters) return undefined;

    const { assets, ...valuesFiltered } = debouncedFilters.values;
    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    const controller = new AbortController();

    api
      .get(datatableDeficienciesPath(), {
        params: kendoDatatableParams({
          page,
          sort,
          filters: { ...debouncedFilters, values: valuesFiltered },
          optionalParams: assets && Array.isArray(assets) && { assets: assets.join(',') }
        }),
        signal: controller.signal
      })
      .then(({ data: deficienciesData }) => dispatch({ type: actionTypes.DATA_LOADED, data: deficienciesData }))
      .catch((err) => {
        if (axios.isCancel(err)) return;
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: false });
      });

    return () => controller.abort();
  }, [page, sort, filters, debouncedFilters]);

  const filtersList = useFilters({
    filters,
    dispatch,
    territoriesForSelect,
    resolutionStatusesForSelect,
    deficiencyStatusesForSelect,
    salesPeopleForSelect,
    assetsForSelect,
    screen
  });

  const onColumnsChange = useCallback(
    (cols) => dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) }),
    []
  );

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTER_CHANGED, filters: getInitialFilters(screen, screenId) });
  };

  const onResetCache = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(cacheKey);
    window.location.reload();
  };

  const onSelectionChange = (event) => {
    dispatch({ type: actionTypes.SELECTION_CHANGED, id: event.dataItem.id });
  };

  const onHeaderSelectionChange = useCallback((event) => {
    dispatch({ type: actionTypes.HEADER_SELECTION_CHANGED, checked: event.syntheticEvent.target.checked });
  }, []);

  const onSelectAll = () => {
    dispatch({ type: actionTypes.ALL_SELECTED });
  };

  const handleDeficiencyClick = useCallback((deficiencyId) => {
    setSelectedDeficiencyId(deficiencyId);
    setIsEmbeddedBrushawayOpen(true);
  }, []);

  const handleEmbeddedBrushawayClose = useCallback(() => {
    setIsEmbeddedBrushawayOpen(false);
  }, []);

  const afterSubmit = () => {
    dispatch({ type: actionTypes.DATA_CHANGED });
  };

  const headerSelectionValue = selectAll || (totalCount > 0 && data.every((inspection) => inspection.selected));

  const DeficiencyIdCellWrapper = useCallback(
    (props) => (
      <DeficiencyIdCell
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        inspectionId={inspectionId}
        onDeficiencyClick={handleDeficiencyClick}
      />
    ),
    [handleDeficiencyClick, inspectionId]
  );

  const allColumnsData = useMemo(
    () => ({
      ...ALL_COLUMNS_DATA,
      id: {
        ...ALL_COLUMNS_DATA.id,
        cell: DeficiencyIdCellWrapper
      }
    }),
    [DeficiencyIdCellWrapper]
  );

  const allMenuColumns = useMemo(() => ALL_COLUMNS.map((column) => allColumnsData[column]), [allColumnsData]);
  const selectedMenuColumns = useMemo(() => columns.map((column) => allColumnsData[column]), [columns, allColumnsData]);
  const visibleColumns = useMemo(
    () => ['selected', ...columns].map((column) => allColumnsData[column]),
    [columns, allColumnsData]
  );

  const columnContextValue = useMemo(
    () => ({
      onColumnsChange,
      columnsState: selectedMenuColumns,
      columns: allMenuColumns,
      resolutionStatusesForSelect,
      deficiencyStatusesForSelect,
      screen,
      screenId,
      afterSubmit
    }),
    [
      onColumnsChange,
      selectedMenuColumns,
      allMenuColumns,
      resolutionStatusesForSelect,
      deficiencyStatusesForSelect,
      screen,
      screenId
    ]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <KendoLocalizationProvider>
        <ColumnMenuContext.Provider value={columnContextValue}>
          <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList} />
          {loading && (
            <div className="workspace__section">
              <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
                <svg role="img">
                  <use href="/map.svg#load-spinner" />
                </svg>
              </div>
            </div>
          )}
          {!loading && (
            <CustomScrollbar>
              <Grid
                data={data}
                total={totalCount}
                skip={page.skip}
                take={page.take}
                onPageChange={(event) => {
                  dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
                }}
                pageable={{
                  type: 'input',
                  pageSizes: [25, 50, 100],
                  pageSizeValue: page.take
                }}
                pager={EnterKeyPager}
                sortable
                sort={sort}
                onSortChange={(event) => {
                  dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
                }}
                dataItemKey="id"
                selectedField="selected"
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
                className="qmb-grid--01">
                <GridToolbar>
                  <MassActions
                    state={state}
                    onSelectAll={onSelectAll}
                    queryParams={kendoDatatableParams({
                      filters: {
                        ...filters,
                        values: Object.fromEntries(Object.entries(filters.values).filter(([key]) => key !== 'assets'))
                      },
                      optionalParams: filters.values.assets &&
                        Array.isArray(filters.values.assets) && { assets: filters.values.assets.join(',') }
                    })}
                  />
                </GridToolbar>
                {visibleColumns.map((column) => (
                  <Column
                    key={column.id}
                    field={column.field}
                    title={column.title}
                    columnMenu={ColumnMenu}
                    cell={column.cell}
                    sortable={column.sortable}
                    headerSelectionValue={headerSelectionValue}
                    className={column.className}
                    width={column.width}
                    headerClassName={column.headerClassName}
                  />
                ))}
              </Grid>
            </CustomScrollbar>
          )}
        </ColumnMenuContext.Provider>
      </KendoLocalizationProvider>
      <EmbeddedDeficiencyDetailBrushaway
        isOpen={isEmbeddedBrushawayOpen}
        deficiencyId={selectedDeficiencyId}
        inspectionId={inspectionId}
        onClose={handleEmbeddedBrushawayClose}
        deficiencyStatuses={deficiencyStatusesForSelect}
        tags={tagsForSelect}
      />
    </QueryClientProvider>
  );
}

DeficienciesIndex.propTypes = {
  territoriesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  resolutionStatusesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  deficiencyStatusesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  tagsForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  salesPeopleForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  assetsForSelect: PropTypes.arrayOf(PropTypes.object),
  screen: PropTypes.string,
  screenId: PropTypes.number,
  cacheKey: PropTypes.string
};

DeficienciesIndex.defaultProps = {
  territoriesForSelect: [],
  resolutionStatusesForSelect: [],
  deficiencyStatusesForSelect: [],
  tagsForSelect: [],
  salesPeopleForSelect: [],
  assetsForSelect: [],
  screen: null,
  screenId: null,
  cacheKey: CACHE_KEY
};

export default DeficienciesIndex;
